import { motion } from "motion/react";
import {
  bool,
  elementType,
  func,
  node,
  number,
  object,
  string,
} from "prop-types";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import styled from "styled-components/macro";

import {
  navSectionKeys,
  useAccountAndSettingsRef,
  useActiveSectionKey,
  useHasActiveSection,
  useIsInitialized,
  useNavBarIsFullyLoaded,
  useNavBarWrapperRef,
  useSetFirstSectionKey,
  useTopAreaRef,
} from "NavBar/navBarStore";
import {
  useEffectiveBgManagerHeight,
  useInactiveSectionSpace,
  useIsFullScreen,
} from "NavBar/stateHooks";
import {
  accountAndSettingsBottomPadding,
  activeSectionMarginBottom,
  activeSectionMarginTop,
  closedNavSectionHeightLg,
  closedNavSectionHeightSm,
  inactiveNavSectionMarginBottom,
  inactiveNavSectionPaddingTop,
  navBarPrimaryContentSpacingY,
  navSectionGap,
  smallViewportHeightBreakpoint,
  springAnimation,
} from "NavBar/styleConstants";

import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { colors } from "UI/colors";

import useHeight from "Utilities/Hooks/useHeight";
import { useWindowSize } from "Utilities/Hooks/useWindowSize";
import dynBackgroundColor from "Utilities/interpolations/dynBackgroundColor";
import dynPadding from "Utilities/interpolations/dynPadding";
import dynShadow from "Utilities/interpolations/dynShadow";
import { defaultShadow, noShadow } from "Utilities/interpolations/shadow";

import ShineWrapper from "../ShineWrapper";
import useLoadGroupsManagementOnScroll from "../useLoadGroupsManagementOnScroll";

import MainContents from "./MainContents";
import SettingsAndHeader from "./SettingsAndHeader";

const navSectionPadding = 12;
const defaultNavSectionMinHeight = 208;
const defaultSettingsAndHeaderWrapperHeight = 38;

const useAnimate = ({ isActive, contentRef, headerHeight }) => {
  const effectiveBgManagerHeight = useEffectiveBgManagerHeight();
  const contentHeight = useHeight(contentRef, 100);

  const inactiveSectionSpace = useInactiveSectionSpace();
  const hasActiveSection = useHasActiveSection();

  const navBarWrapperRef = useNavBarWrapperRef();
  const topAreaRef = useTopAreaRef();
  const accountAndSettingsRef = useAccountAndSettingsRef();

  const wrapperHeight = useHeight(navBarWrapperRef, 500);
  const topAreaHeight = useHeight(topAreaRef, 500);
  const accountAndSettingsHeight = useHeight(accountAndSettingsRef, 500);

  const { height: viewportHeight } = useWindowSize();
  const isSmallViewport = viewportHeight < smallViewportHeightBreakpoint;
  const navSectionMinHeight = isSmallViewport
    ? Infinity
    : defaultNavSectionMinHeight;

  const maxHeight = useMemo(() => {
    return Math.max(
      wrapperHeight -
        topAreaHeight -
        accountAndSettingsHeight -
        accountAndSettingsBottomPadding -
        effectiveBgManagerHeight -
        inactiveSectionSpace -
        activeSectionMarginTop -
        activeSectionMarginBottom -
        navBarPrimaryContentSpacingY,
      navSectionMinHeight
    );
  }, [
    accountAndSettingsHeight,
    effectiveBgManagerHeight,
    inactiveSectionSpace,
    navSectionMinHeight,
    topAreaHeight,
    wrapperHeight,
  ]);

  return useMemo(() => {
    const borderRadius = isActive ? 20 : 8;
    let height;
    if (isActive) {
      height = Math.min(
        maxHeight,
        headerHeight + navSectionGap + contentHeight
      );
    } else
      height = hasActiveSection
        ? closedNavSectionHeightSm
        : closedNavSectionHeightLg;

    let marginBottom;
    let marginTop;
    if (!hasActiveSection) {
      marginBottom = 0;
      marginTop = 0;
    } else if (isActive) {
      marginBottom = activeSectionMarginBottom;
      marginTop = activeSectionMarginTop;
    } else if (!isActive) {
      marginBottom = inactiveNavSectionMarginBottom;
      marginTop = 0;
    }

    return {
      height,
      marginTop,
      marginBottom,
      borderRadius,
    };
  }, [contentHeight, hasActiveSection, headerHeight, isActive, maxHeight]);
};

const springTransition = { height: springAnimation };
const instantTransition = { duration: 0 };

const NavSection = ({
  icon,
  label,
  color,
  SettingsContent,
  children,
  navSectionKey,
  count,
  settingsCount,
  isFirst,
  settingsSelected,
  scrollRef: scrollRefArg,
  directSettingsAction,
  headerTextRef,
  isSquiggleFlashing,
  settingsButtonRef,
  isSettingsFlashing,
  onHeaderMouseEnter,
  defaultRoute,
  bottomPadding = 0,
  HeaderSuffixComponent,
  ...rest
}) => {
  const [activeSectionKey, setActiveSectionKey] = useActiveSectionKey();
  const isInitialized = useIsInitialized();
  const isFullyLoaded = useNavBarIsFullyLoaded();
  const isFullScreen = useIsFullScreen();
  const setFirstSectionKey = useSetFirstSectionKey();

  useEffect(() => {
    if (isFirst) setFirstSectionKey(navSectionKey);
  }, [isFirst, navSectionKey, setFirstSectionKey]);
  const wrapperRef = useRef();
  const contentRef = useRef();
  const localScrollRef = useRef();
  const scrollRef = scrollRefArg || localScrollRef;
  const settingsAndHeaderWrapperRef = useRef();

  // Use instant transition until initialized to prevent animation on page load
  const transition = isInitialized ? springTransition : instantTransition;

  const isActive = useMemo(
    () => navSectionKey === activeSectionKey,
    [activeSectionKey, navSectionKey]
  );

  const buttonRef = useRef();

  const headerHeight =
    useHeight(settingsAndHeaderWrapperRef, 100) ||
    defaultSettingsAndHeaderWrapperHeight;

  const animate = useAnimate({
    isActive,
    contentRef,
    headerHeight,
  });

  const handleKeyDown = useCallback(
    (e) => {
      if (
        e.key === "Escape" &&
        isActive &&
        !document.querySelector("[data-isopensettingsbutton=true]")
      ) {
        setActiveSectionKey(navSectionKeys.nothing);
        if (!isFullScreen) window.fnReactBus.goToUrl("/action-center");
        buttonRef.current.focus();
      }
    },
    [isActive, isFullScreen, setActiveSectionKey]
  );

  useLoadGroupsManagementOnScroll(scrollRef);

  const mainContentsMaxHeight = useMemo(() => {
    if (isActive)
      return `${animate.height - headerHeight - navSectionGap + 2}px`;
    return "0px";
  }, [animate.height, headerHeight, isActive]);

  const padding = useMemo(
    () =>
      `${isFirst || isActive ? 0 : inactiveNavSectionPaddingTop}px 0 ${
        isActive ? bottomPadding : 0
      }px 0`,
    [bottomPadding, isActive, isFirst]
  );

  const backgroundColor = useMemo(
    () => (isActive ? color : colors.transparent),
    [color, isActive]
  );

  const shadow = useMemo(
    () => (isActive ? defaultShadow : noShadow),
    [isActive]
  );

  return (
    <FnrOpacityToggle show={isInitialized && isFullyLoaded} delayIn={300}>
      <ShineWrapper isActive={isActive} onKeyDown={handleKeyDown}>
        <MotionWrapper
          ref={wrapperRef}
          animate={animate}
          transition={transition}
          $padding={padding}
          $backgroundColor={backgroundColor}
          $shadow={shadow}
          initial={false}
          {...rest}
        >
          <StaticWrapper>
            <SettingsAndHeader
              {...{
                count,
                settingsCount,
                isActive,
                navSectionKey,
                buttonRef,
                headerTextRef,
                color,
                icon,
                label,
                SettingsContent,
                settingsAndHeaderWrapperRef,
                settingsSelected,
                directSettingsAction,
                isSquiggleFlashing,
                settingsButtonRef,
                isSettingsFlashing,
                onHeaderMouseEnter,
                defaultRoute,
                HeaderSuffixComponent,
              }}
            />
            <MainContents
              show={isActive}
              maxHeight={mainContentsMaxHeight}
              scrollRef={scrollRef}
              contentRef={contentRef}
              navSectionKey={navSectionKey}
            >
              {children}
            </MainContents>
          </StaticWrapper>
        </MotionWrapper>
      </ShineWrapper>
    </FnrOpacityToggle>
  );
};

NavSection.propTypes = {
  icon: string,
  label: string,
  color: string,
  SettingsContent: elementType,
  children: node,
  navSectionKey: string,
  count: number,
  settingsCount: number,
  isFirst: bool,
  settingsSelected: bool,
  scrollRef: object,
  directSettingsAction: func,
  headerTextRef: object,
  isSquiggleFlashing: bool,
  settingsButtonRef: object,
  isSettingsFlashing: bool,
  onHeaderMouseEnter: func,
  defaultRoute: string,
  bottomPadding: number,
  HeaderSuffixComponent: elementType,
};

const MotionWrapper = memo(styled(motion.div)`
  overflow: clip;
  ${dynPadding}
  ${dynBackgroundColor}
  ${dynShadow}
  transition: background-color 300ms;
`);

const StaticWrapper = styled.div`
  display: flex;
  gap: ${navSectionGap}px;
  flex-direction: column;
`;

export { navSectionPadding };
export default NavSection;
